import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { CaptainAdvance, CaptainDTO } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { createSubtleIceSurface } from '../../theme/surfaces';
import { CAPTAIN_MAX_LEVEL, getCaptainSharePercent } from '../../utils/captainRules';

interface ManageCaptainLevelsModalProps {
  open: boolean;
  captain: CaptainDTO;
  onClose: () => void;
}

export const ManageCaptainLevelsModal: React.FC<ManageCaptainLevelsModalProps> = ({ open, captain, onClose }) => {
  const tricks = useReferenceDataStore((s) => s.tricks);
  const updateCaptainProgression = useDirtyTrackingStore((s) => s.updateCaptainProgression);

  const [xpInput, setXpInput] = React.useState(String(captain.experience));
  const [experience, setExperience] = React.useState(captain.experience);
  const [advances, setAdvances] = React.useState<CaptainAdvance[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setExperience(captain.experience);
    setXpInput(String(captain.experience));
    setAdvances(captain.advances);
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const earnedLevels = Math.min(Math.floor(experience / 100), CAPTAIN_MAX_LEVEL);
  const currentLevel = Math.min(advances.length, CAPTAIN_MAX_LEVEL);

  const getAdvanceLabel = (advance: CaptainAdvance): string => {
    if (advance.advance === 'health') return '+1 Health';
    if (advance.advance === 'will') return '+1 Will';
    const trick = tricks.find((t) => t.id === advance.trickId);
    return `Trick: ${trick?.name ?? advance.trickId ?? 'Unknown'}`;
  };

  const clearRow = (index: number) => {
    setAdvances((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExperienceChange = (xp: number) => {
    setExperience(xp);
    // Drop advances that are no longer covered by earned levels
    const allowed = Math.min(Math.floor(xp / 100), CAPTAIN_MAX_LEVEL);
    setAdvances((prev) => (prev.length > allowed ? prev.slice(0, allowed) : prev));
  };

  const handleSave = () => {
    if (experience < 0) {
      setError('Experience cannot be negative.');
      return;
    }
    updateCaptainProgression(experience, advances);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Captain Levels</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Experience (XP)"
          type="number"
          value={xpInput}
          onChange={(e) => setXpInput(e.target.value)}
          onBlur={() => {
            const parsed = Math.max(0, parseInt(xpInput, 10) || 0);
            setXpInput(String(parsed));
            handleExperienceChange(parsed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const parsed = Math.max(0, parseInt(xpInput, 10) || 0);
              setXpInput(String(parsed));
              handleExperienceChange(parsed);
              (e.target as HTMLInputElement).blur();
            }
          }}
          fullWidth
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { min: 0 } }}
        />
        <Typography variant="body2" color="text.secondary">
          {captain.name} is level {currentLevel} with {earnedLevels - currentLevel} unspent level-up{earnedLevels - currentLevel === 1 ? '' : 's'} (100 XP each, maximum level {CAPTAIN_MAX_LEVEL}).
          Share of the treasure: {getCaptainSharePercent(currentLevel)}%.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Unspent level-ups are applied with the Level Up button on the captain's card.
        </Typography>

        <Accordion disableGutters defaultExpanded sx={{ ...createSubtleIceSurface(500, {}) }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Level Choices
              {earnedLevels > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({advances.length}/{earnedLevels} recorded)
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {earnedLevels === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No level-ups earned yet (level {currentLevel}).
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Array.from({ length: earnedLevels }, (_, i) => {
                  const advance = advances[i];
                  return (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ minWidth: 72 }}>Level {i + 1}</Typography>
                        {advance ? (
                          <>
                            <Chip label={getAdvanceLabel(advance)} size="small" />
                            <IconButton size="small" onClick={() => clearRow(i)}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </Box>
                      {i < earnedLevels - 1 && <Divider sx={{ mt: 1 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
